/**
 * Scroll Direction Hook
 *
 * Detects scroll direction and visibility state for sticky headers
 * Used in Detail pages to show/hide header based on scroll behavior
 */

import { useState, useEffect } from 'react'

interface UseScrollDirectionOptions {
  threshold?: number  // Minimum scroll distance to trigger direction change
  initialDirection?: 'up' | 'down'
  /** When false, skip scroll listener entirely (no re-renders). Defaults to true. */
  enabled?: boolean
}

interface ScrollState {
  scrollDirection: 'up' | 'down'
  isScrolledPast: boolean  // Has scrolled past the initial header
  isVisible: boolean       // Should header be visible
}

export const useScrollDirection = (options: UseScrollDirectionOptions = {}): ScrollState => {
  const { threshold = 10, initialDirection = 'up', enabled = true } = options

  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollDirection: initialDirection,
    isScrolledPast: false,
    isVisible: true
  })

  useEffect(() => {
    if (!enabled) return

    let lastScrollY = window.pageYOffset
    let ticking = false

    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset
      const diff = scrollY - lastScrollY
      const isScrolledPast = scrollY > 48 // Past GlobalNav height (h-12)

      // Only update state if scroll distance exceeds threshold
      if (Math.abs(diff) < threshold) {
        ticking = false
        return
      }

      const direction = diff > 0 ? 'down' : 'up'

      // Always update lastScrollY to prevent accumulation lag
      lastScrollY = scrollY > 0 ? scrollY : 0

      // Determine visibility based on scroll behavior
      const isVisible = isScrolledPast ? direction === 'up' : true

      setScrollState(prev => {
        // Skip re-render if nothing changed
        if (prev.scrollDirection === direction && prev.isScrolledPast === isScrolledPast && prev.isVisible === isVisible) {
          ticking = false
          return prev
        }
        return { scrollDirection: direction, isScrolledPast, isVisible }
      })

      ticking = false
    }

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollDirection)
        ticking = true
      }
    }

    window.addEventListener('scroll', requestTick)

    return () => {
      window.removeEventListener('scroll', requestTick)
    }
  }, [threshold, enabled])

  return scrollState
}